import { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";

type CountdownProps = {
  endTime: string | Date;
  isMobile: boolean;
};

const padZero = (num: number) => String(num).padStart(2, "0");

const parseDate = (input: string | Date): Date => {
  if (input instanceof Date) return input;

  const str = input.trim();

  // ISO 或浏览器可解析格式
  const iso = new Date(str);
  if (!isNaN(iso.getTime())) return iso;
  return new Date(NaN); // fallback
};

const getTimeLeft = (end: Date) => {
  const total = end.getTime() - new Date().getTime();
  const clamped = Math.max(0, total);
  const seconds = Math.floor((clamped / 1000) % 60);
  const minutes = Math.floor((clamped / 1000 / 60) % 60);
  const hours = Math.floor((clamped / (1000 * 60 * 60)) % 24);
  const days = Math.floor(clamped / (1000 * 60 * 60 * 24));
  return { total: clamped, days, hours, minutes, seconds };
};

export default function Countdown({ endTime, isMobile }: CountdownProps) {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(parseDate(endTime)));

  useEffect(() => {
    const timer = setInterval(() => {
      const updated = getTimeLeft(parseDate(endTime));
      setTimeLeft(updated);
      if (updated.total <= 0) clearInterval(timer);
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime]);

  const renderBlock = (value: string, label: string) => {
    const digitLength = value.length;

    // 控制数字区域最小宽度
    const digitWidthMap = {
      1: "min-w-[40px]",
      2: "min-w-[77px]",
      3: "min-w-[102px]",
    };

    const digitWidthMapMobile = {
      1: "min-w-[40px]",
      2: "min-w-[48px]",
      3: "min-w-[70px]",
    };

    const digitWidthClass = digitWidthMap[digitLength] || "min-w-[102px]";
    const placeholder = "8".repeat(digitLength);

    return (
      <div className="rounded-2xl bg-gradient-to-b from-[#df2c2766] to-[#df2c2719] p-[1px]">
        <div
          className={`text-red-600 flex flex-col items-center rounded-2xl bg-white text-center shadow  
            ${isMobile ? "p-2" : "p-3"}`}
        >
          <div
            className={`relative font-[Poppins] font-medium tabular-nums leading-[1.2] text-primary
            ${isMobile ? digitWidthMapMobile : digitWidthClass} 
            ${isMobile ? "h-[50px] text-[40px]" : "h-[72px] text-[64px]"}`}
          >
            {/* 占位数字撑宽度 */}
            <span className="invisible">{placeholder}</span>
            {/* 居中真实值 */}
            <span className="absolute inset-0 flex items-center justify-center">{value}</span>
          </div>
          <div className={`text-sm text-[#4b5563] ${isMobile ? "" : "mt-1"}`}>{label}</div>
        </div>
      </div>
    );
  };

  return (
    <div className={`flex ${isMobile ? "space-x-2" : "space-x-4"}`}>
      {renderBlock(padZero(timeLeft.days), t("csr.countdown_day"))}
      {renderBlock(padZero(timeLeft.hours), t("csr.countdown_hour"))}
      {renderBlock(padZero(timeLeft.minutes), t("csr.countdown_minute"))}
      {renderBlock(padZero(timeLeft.seconds), t("csr.countdown_second"))}
    </div>
  );
}
